import { Pressable, StyleSheet, View } from 'react-native';

import { assertHitOnLayout } from '../a11y';
import { colors, hit, radii, strokes, tint } from '../tokens';
import { Text } from './Text';

export interface ChipProps {
  label: string;
  selected?: boolean;
  onPress?: () => void;
  /** On the turquoise header (P07): unselected chips are translucent white with white text. On the neutral body (P05): white, quiet border, slate text. */
  surface?: 'neutral' | 'turquoise';
  testID?: string;
}

/** Filter chip: 32px pill (6×14 padding, 13px) inside a 44px-tall Pressable box. Selected = ink fill, white 700 label. */
export function Chip({ label, selected = false, onPress, surface = 'neutral', testID }: ChipProps) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      onLayout={assertHitOnLayout(`Chip "${label}"`, hit.parent)}
      testID={testID}
      style={styles.box}
    >
      <View
        style={[
          styles.pill,
          selected
            ? styles.selected
            : surface === 'turquoise'
              ? styles.translucent
              : styles.neutral,
        ]}
      >
        <Text
          variant={selected ? 'chipSelected' : 'chip'}
          tone={selected || surface === 'turquoise' ? 'white' : 'slate'}
          numberOfLines={1}
        >
          {label}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  box: { minHeight: hit.parent, justifyContent: 'center', alignSelf: 'flex-start' },
  pill: {
    // Grows with the phone's text size (it was a fixed 32, which clipped at the largest sizes).
    minHeight: 32,
    paddingVertical: 5,
    paddingHorizontal: 14,
    borderRadius: radii.pill,
    alignItems: 'center',
    justifyContent: 'center',
  },
  selected: { backgroundColor: colors.ink },
  neutral: {
    backgroundColor: colors.white,
    borderWidth: strokes.quiet,
    borderColor: colors.quietBorder,
  },
  translucent: { backgroundColor: tint.white25 },
});
